import { useState } from "react";
import { Menu } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { ThemeToggle } from "./ThemeToggle";
import { theme } from "@/theme/theme";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme: themeMode } = useTheme();
  const navStyles = theme.navigation[themeMode];
  const navFont = "font-mono font-medium tracking-tight";

  const navItems = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About" },
    { href: "#experience", label: "Experience" },
    { href: "#skills", label: "Skills" },
    // { href: "#projects", label: "Projects" },
    { href: "#services", label: "Services" },
    { href: "#contact", label: "Contact" },
  ];

  // Smooth scroll handler
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      // Update the URL hash without jumping
      window.history.pushState(null, "", href);
    }
    if (isOpen) setIsOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 ${navStyles.background} transition-colors duration-300`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 relative flex items-center justify-center">
              {/* Fading Gradient Glow */}
              <div
                className="absolute inset-0 rounded-full pointer-events-none z-0"
                style={{
                  background: navStyles.logo.glow,
                  filter: "blur(2px)",
                }}
              />
              <img
                src={
                  themeMode === "dark"
                    ? "/images/white-logo.png"
                    : "/images/logo-svg.svg"
                }
                alt="Logo"
                className={`w-12 h-12 drop-shadow-lg filter z-10 ${navStyles.logo.shadow}`}
              />
            </div>
            <div
              className={`text-xl ${navFont} ${navStyles.text.main} flex items-center`}
            >
              <span className={`${navStyles.text.accent} font-bold`}>
                Adarsh
              </span>
              <span className="ml-1">Parmar</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`
                  ${navStyles.text.main} ${navFont} transition-all duration-200 px-3 py-1.5 rounded-none relative
                  after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:w-0 after:h-0.5 after:bg-current after:transition-all after:duration-300 hover:after:w-full
                  ${navStyles.text.hover}
                  hover:-translate-y-0.5 hover:drop-shadow-md
                `}
                style={{ overflow: "hidden" }}
              >
                {item.label}
              </a>
            ))}
            <div className="ml-4">
              <ThemeToggle />
            </div>
          </div>

          {/* Mobile menu button and theme toggle */}
          <div className="flex items-center md:hidden gap-3">
            <ThemeToggle />
            <button
              className={`${navStyles.text.accent} p-1.5 rounded-md transition-transform duration-300`}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Open navigation menu"
            >
              <Menu
                size={24}
                className="transition-transform duration-300"
                style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
              />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`
            md:hidden ${navStyles.background} overflow-hidden w-full
            transition-all duration-500 ease-custom-ease
            transform-gpu origin-top
            ${
              isOpen
                ? "max-h-[500px] opacity-100 scale-y-100"
                : "max-h-0 opacity-0 scale-y-0"
            }
          `}
          style={{
            transform: isOpen
              ? "perspective(1000px) rotateX(0deg)"
              : "perspective(1000px) rotateX(-15deg)",
            transformOrigin: "top",
            transition: "all 500ms cubic-bezier(0.4, 0, 0.2, 1)",
            borderTop: navStyles.mobile.border,
            marginLeft: "-1rem",
            marginRight: "-1rem",
            width: "calc(100% + 2rem)",
          }}
        >
          <div className="py-2">
            {navItems.map((item, index) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`
                  ${navStyles.text.main} ${navFont} block px-6 py-3 transition-all duration-200 relative
                  after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:w-0 after:h-0.5 after:bg-current after:transition-all after:duration-300 hover:after:w-full
                  ${navStyles.text.hover}
                  hover:-translate-y-0.5 hover:drop-shadow-md
                `}
                style={{
                  overflow: "hidden",
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(-10px)",
                  transition: `all 300ms cubic-bezier(0.4, 0, 0.2, 1) ${
                    index * 50
                  }ms`,
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
